import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaGithub, FaMicrochip, FaBatteryFull, FaRobot, FaCloud, FaBroadcastTower, FaExternalLinkAlt, FaChevronRight } from 'react-icons/fa'
import './Portfolio.css'

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('all')

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'bms', label: 'BMS & EV' },
    { id: 'embedded', label: 'Embedded Systems' },
    { id: 'iot', label: 'IoT & Cloud' },
    { id: 'robotics', label: 'Robotics & Drone' },
    { id: 'ai', label: 'AI & Edge' },
  ]

  const projects = [
    {
      id: 1,
      title: 'Smart Shower – Edge AI Water Wastage Detection',
      subtitle: 'KAUST – Freelance & Visiting Student',
      category: 'ai',
      icon: <FaBroadcastTower />,
      period: '09/2025 – 11/2025',
      tags: ['nRF52840', 'FreeRTOS', 'Edge Impulse', 'BLE', 'macOS App', 'PCB Design'],
      description: 'AI-powered smart shower system that detects water wastage through real-time microphone sensor analysis, deployed on Nordic nRF52840.',
      highlights: [
        'Developed Edge AI models using Edge Impulse for on-device water wastage detection',
        'Built native macOS app with BLE connectivity for data collection, labeling, and playback',
        'Designed FreeRTOS-based firmware with BLE-connected mobile app for shower usage analytics',
        'Designed custom PCB shield with SD card interface for audio data collection',
      ],
      color: '#06b6d4',
    },
    {
      id: 2,
      title: 'Wake-Up Radio for Ultra-Low-Power IoT',
      subtitle: 'KAUST – Visiting Student Research',
      category: 'iot',
      icon: <FaBroadcastTower />,
      period: '09/2025 – 11/2025',
      tags: ['AS3933', 'ESP32', 'SX1278', 'STM32', 'Low Power'],
      description: 'Wake-up radio system for activating ultra-low-power IoT nodes in remote pipeline monitoring environments.',
      highlights: [
        'Integrated AS3933 wake-up receiver with ESP32 for ultra-low-power node activation',
        'Implemented SX1278 LoRa transceiver on STM32-based transmitter for long-range communication',
        'Designed for remote pipeline monitoring with minimal power consumption',
      ],
      color: '#8b5cf6',
    },
    {
      id: 3,
      title: 'Electric Scooter BMS',
      subtitle: 'AFPM Technology',
      category: 'bms',
      icon: <FaBatteryFull />,
      period: '06/2022 – 09/2025',
      tags: ['STM32', 'BQ76940', 'BQ34z100', 'CAN Bus', 'Embedded C'],
      description: 'Battery Management System for electric scooters with dual-level protection and CAN bus communication with vehicle ECU.',
      highlights: [
        'Implemented 2-level battery protection using BQ76940 and BQ34z100 ICs',
        'Developed CAN bus communication protocol with vehicle ECU',
        'Real-time SoC monitoring and cell balancing algorithms',
      ],
      color: '#10b981',
    },
    {
      id: 4,
      title: 'Modular Industrial Vehicle BMS',
      subtitle: 'AFPM Technology',
      category: 'bms',
      icon: <FaBatteryFull />,
      period: '06/2022 – 09/2025',
      tags: ['STM32', 'LTC6813', 'LTC6811', 'Simulink', 'EKF', 'CAN Bus'],
      description: 'Scalable BMS for golf carts and boats with LiFePO4 battery modeling and State of Charge estimation via Extended Kalman Filter.',
      highlights: [
        'Modular architecture supporting LTC6813/LTC6811 ICs for multi-cell configurations',
        'LiFePO4 battery modeling in Simulink with EKF-based SoC estimation',
        'CAN bus integration with motor controllers (e.g., Zapi)',
      ],
      color: '#10b981',
    },
    {
      id: 5,
      title: 'CAN Bus Bootloader',
      subtitle: 'AFPM Technology',
      category: 'embedded',
      icon: <FaMicrochip />,
      period: '06/2022 – 09/2025',
      tags: ['STM32', 'CAN Bus', 'Python', 'Bootloader', 'Embedded C'],
      description: 'Custom CAN bus bootloader for over-the-wire firmware updates across BMS devices.',
      highlights: [
        'Embedded bootloader on STM32 with CAN bus firmware reception',
        'Python PC-side application for firmware packaging and deployment',
        'Secure and reliable firmware update mechanism',
      ],
      github: 'https://github.com/fouad1233',
      color: '#f59e0b',
    },
    {
      id: 6,
      title: 'Electric Car Tracking System',
      subtitle: 'Senior Year Project',
      category: 'iot',
      icon: <FaCloud />,
      period: '06/2024 – 06/2025',
      tags: ['STM32U575', 'ThreadX', 'Azure IoT', '4G/LTE', 'GPS', 'FOTA', 'CAN Bus'],
      description: 'IoT-based EV tracking system with Azure IoT Central integration, cellular connectivity, and firmware over-the-air updates.',
      highlights: [
        'STM32U575 with ThreadX RTOS publishing CAN bus data to Azure IoT Central',
        'Quectel EG915 4G/LTE module for mobile connectivity',
        'GPS tracking with Quectel L26ADR and IMU sensor fusion (LIS3MDL + LSM6DSOX)',
        'CAN bus bootloader with FOTA for remote firmware updates',
      ],
      color: '#3b82f6',
    },
    {
      id: 7,
      title: 'GTU ROVER – Autonomous Drone',
      subtitle: 'Drone Team Leader',
      category: 'robotics',
      icon: <FaRobot />,
      period: '06/2022 – 06/2024',
      tags: ['ArduPilot', 'ROS', 'MAVROS', 'OpenCV', 'PID', 'Python'],
      description: 'Autonomous drone software system for the University Rover Challenge with ArUco marker precision landing capabilities.',
      highlights: [
        'Built autonomous flight stack using ArduPilot + ROS + MAVROS for MAVLink bridge',
        'Implemented OpenCV-based ArUco code recognition for target identification',
        'Developed PID-controlled precision landing algorithm',
        'Competed in Anatolian Rover Challenge and University Rover Challenge',
      ],
      github: 'https://github.com/fouad1233/drone_ws',
      color: '#ef4444',
    },
    {
      id: 10,
      title: 'GTU ROVER – Science Platform',
      subtitle: 'Electronics Team Member',
      category: 'robotics',
      icon: <FaRobot />,
      period: '06/2022 – 06/2024',
      tags: ['ESP32', 'ESPNow', 'Sensors', 'Web Interface', 'Stepper Motors'],
      description: 'Wireless science platform for environmental data collection, built for the University Rover Challenge.',
      highlights: [
        'ESP32-powered platform measuring CO, CO₂, O₂, humidity, temperature, and altitude',
        'Wireless data transmission to base station via ESPNow protocol',
        'Integrated stepper motor, valve, and pump control through a web interface',
        'Competed in Anatolian Rover Challenge and University Rover Challenge',
      ],
      github: 'https://github.com/fouad1233/science_platform',
      color: '#f97316',
    },
    {
      id: 8,
      title: 'STM32G0 Tutorial Series',
      subtitle: 'Open-Source Educational Project',
      category: 'embedded',
      icon: <FaMicrochip />,
      period: '11/2023 – 02/2024',
      tags: ['STM32G0', 'ARM Assembly', 'C', 'Register Level', 'Open Source'],
      description: 'Comprehensive register-level tutorial series for STM32G0, covering peripherals from GPIO to I2C, published on GitHub.',
      highlights: [
        'ARM Assembly and C register-level examples for beginners',
        'GPIO, timers, interrupts, SysTick, watchdog, PWM, UART, I2C',
        'MPU6050 knock detection and sound sensor projects',
        'Published as open-source educational resource',
      ],
      github: 'https://github.com/fouad1233',
      color: '#f59e0b',
    },
    {
      id: 11,
      title: 'Zephyr RTOS Tutorials',
      subtitle: 'Open-Source Educational Project',
      category: 'embedded',
      icon: <FaMicrochip />,
      period: '06/2024 – 07/2024',
      tags: ['Zephyr RTOS', 'STM32', 'MCUBoot', 'C', 'Custom Board', 'Open Source'],
      description: 'Comprehensive Zephyr RTOS tutorial series on STM32, covering peripherals, RTOS concepts, custom board definitions, and MCUBoot encrypted firmware updates.',
      highlights: [
        'LED blink, GPIO interrupts, task creation, and knight rider examples',
        'PWM control and ADC reading tutorials',
        'Custom board definition for STG-800 PLC (STM32F091)',
        'MCUBoot bootloader with encrypted firmware image support',
      ],
      github: 'https://github.com/fouad1233/zephyr_rtos_tutuorials',
      color: '#14b8a6',
    },
    {
      id: 12,
      title: 'DC Motor Speed Control',
      subtitle: 'Control Systems Project',
      category: 'embedded',
      icon: <FaMicrochip />,
      period: '2024',
      tags: ['MATLAB', 'Simulink', 'Control Systems', 'Compensator Design', 'Root Locus', 'DC Motor'],
      description: 'Analytical and simulation-based DC motor speed control using lead, lag, and lead-lag compensators with root locus design in MATLAB/Simulink.',
      highlights: [
        'Modeled DC motor transfer function from electrical and mechanical parameters',
        'Designed lead, lag, and lead-lag compensators using root locus analysis',
        'Simulated closed-loop speed control in Simulink',
        'Documented with a full project report',
      ],
      github: 'https://github.com/fouad1233/DC_Motor_Speed_Control',
      color: '#a855f7',
    },
    {
      id: 13,
      title: 'STM32 Auto Build & Versioning with GitHub Actions',
      subtitle: 'CI/CD for Embedded Systems',
      category: 'embedded',
      icon: <FaMicrochip />,
      period: '2025',
      tags: ['STM32', 'GitHub Actions', 'CI/CD', 'Docker', 'STM32CubeIDE', 'Automated Build'],
      description: 'Automated CI/CD pipeline for STM32 projects using GitHub Actions with auto-versioning, Docker-based STM32CubeIDE headless build, and artifact management.',
      highlights: [
        'GitHub Actions workflow for automated STM32F4 firmware compilation',
        'Docker-based STM32CubeIDE headless build toolchain',
        'Automated semantic versioning and binary artifact generation',
        'USB Host Library and HAL driver integration',
      ],
      github: 'https://github.com/fouad1233/stm32-auto-build-versioning-github-actions',
      color: '#f97316',
    },
    {
      id: 14,
      title: 'Traffic Sign Detection',
      subtitle: 'Computer Vision Project',
      category: 'ai',
      icon: <FaBroadcastTower />,
      period: '2024',
      tags: ['Python', 'PIL', 'Image Preprocessing', 'Computer Vision', 'Dataset Preparation'],
      description: 'Traffic sign detection dataset preparation pipeline with image preprocessing, grayscale conversion, and organized dataset structure for model training.',
      highlights: [
        'Built image preprocessing pipeline with color-to-grayscale conversion',
        'Organized dataset structure for training traffic sign classifiers',
        'Batch image processing using PIL for large-scale dataset augmentation',
      ],
      github: 'https://github.com/fouad1233/traffic_sign_detection',
      color: '#ef4444',
    },
    {
      id: 15,
      title: 'Image Processing Tutorials',
      subtitle: 'Academic Course Projects',
      category: 'ai',
      icon: <FaBroadcastTower />,
      period: '2024',
      tags: ['Python', 'NumPy', 'OpenCV', 'FFT', 'Filtering', 'Segmentation', 'Histogram'],
      description: 'Comprehensive image processing tutorial series covering PGM read/write, spatial filtering, histogram equalization, frequency domain analysis, and segmentation — all built from scratch.',
      highlights: [
        'Built custom Image_analyzer library from scratch with NumPy',
        'Implemented correlation, median filtering, and Gaussian filters',
        'DFT/FFT-based frequency domain analysis and filtering',
        'Otsu\'s thresholding and local adaptive segmentation',
        'Haar Cascade face detection with glasses recognition project',
      ],
      github: 'https://github.com/fouad1233/image_processing_tutorials',
      color: '#3b82f6',
    },
    {
      id: 16,
      title: 'Machine Learning Tutorials',
      subtitle: 'Academic Course Projects',
      category: 'ai',
      icon: <FaBroadcastTower />,
      period: '2024',
      tags: ['Python', 'NumPy', 'Scikit-learn', 'Regression', 'KNN', 'Decision Tree', 'PCA', 'Perceptron'],
      description: 'End-to-end machine learning tutorial series covering regression, KNN, Naive Bayes, decision trees (Python & C++), PCA dimensionality reduction, and perceptron neural networks.',
      highlights: [
        'Linear & polynomial regression with MSE and classification metrics',
        'KNN clustering and spam mail detection with Naive Bayes',
        'Decision tree classifier built from scratch in Python and C++',
        'PCA dimensionality reduction built from scratch on Iris & digit datasets',
        'Perceptron regression with sigmoid activation',
      ],
      github: 'https://github.com/fouad1233/machine_learning_tutorials',
      color: '#8b5cf6',
    },
    {
      id: 9,
      title: 'TÜBİTAK 2209-B – BMS for Micro EVs',
      subtitle: 'University-Industry Collaboration',
      category: 'bms',
      icon: <FaBatteryFull />,
      period: '03/2023 – 03/2024',
      tags: ['BMS', 'SoC Estimation', 'Micro EV', 'TÜBİTAK'],
      description: 'Government-funded research project developing BMS and State of Charge estimation for micro electric vehicles.',
      highlights: [
        'TÜBİTAK-funded university-industry collaboration with AFPM Technology',
        'Battery Management System design for micro electric vehicles',
        'State of Charge estimation algorithm development',
      ],
      color: '#10b981',
    },
  ]

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(p => p.category === activeFilter)

  return (
    <div className="portfolio">
      {/* Hero */}
      <section className="portfolio-hero section-padding">
        <div className="container">
          <motion.div
            className="portfolio-hero-content text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>My Portfolio</h1>
            <p className="portfolio-hero-subtitle">
              A collection of projects spanning embedded systems, BMS development, Edge AI, IoT, and robotics — built with real hardware and real impact.
            </p>
            <div className="portfolio-stats">
              <div className="portfolio-stat">
                <span className="portfolio-stat-number">16+</span>
                <span className="portfolio-stat-label">Projects</span>
              </div>
              <div className="portfolio-stat-divider" />
              <div className="portfolio-stat">
                <span className="portfolio-stat-number">3+</span>
                <span className="portfolio-stat-label">Years Experience</span>
              </div>
              <div className="portfolio-stat-divider" />
              <div className="portfolio-stat">
                <span className="portfolio-stat-number">5</span>
                <span className="portfolio-stat-label">Domains</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="portfolio-filters section-padding bg-secondary">
        <div className="container">
          <motion.div
            className="filter-bar"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`filter-btn ${activeFilter === cat.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="portfolio-projects section-padding">
        <div className="container">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              className="projects-masonry"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className="portfolio-card"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="portfolio-card-accent" style={{ background: project.color }} />
                  <div className="portfolio-card-header">
                    <div className="portfolio-card-icon" style={{ color: project.color }}>
                      {project.icon}
                    </div>
                    <div className="portfolio-card-meta">
                      <span className="portfolio-card-period">{project.period}</span>
                    </div>
                  </div>

                  <h3 className="portfolio-card-title">{project.title}</h3>
                  <p className="portfolio-card-subtitle">{project.subtitle}</p>
                  <p className="portfolio-card-description">{project.description}</p>

                  <ul className="portfolio-card-highlights">
                    {project.highlights.map((h, i) => (
                      <li key={i}>
                        <FaChevronRight className="highlight-arrow" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="portfolio-card-footer">
                    <div className="portfolio-card-tags">
                      {project.tags.map((tag, i) => (
                        <span key={i} className="portfolio-tag">{tag}</span>
                      ))}
                    </div>
                    {project.github && (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="portfolio-card-link"
                      >
                        <FaGithub /> <span>View Code</span> <FaExternalLinkAlt className="link-external" />
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {filteredProjects.length === 0 && (
            <motion.p
              className="no-projects text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              No projects in this category yet.
            </motion.p>
          )}
        </div>
      </section>
    </div>
  )
}

export default Portfolio
